import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useForm, Controller } from "react-hook-form";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useNavigate } from "react-router";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useInvoiceCancellationListAPI } from "@/app/hooks/api_hooks/Cart/useInvoiceCancellationListAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useInvoiceCancelAPI } from "@/app/hooks/api_hooks/Cart/useInvoiceCancelAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInvoiceProps, useCartInvoiceListAPI } from "@/app/hooks/api_hooks/Cart/useCartInvoiceListAPI";
import {
  ICartState,
  RESET_INVOICE_CART_SLICE,
  setActiveCartId,
  setIsInvoiceLoad,
} from "@/app_redux/reducers/slices/auth/cart_slice";
import { useDispatch } from "react-redux";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  setInitialState?: (value: boolean) => void;
  invoiceId: string;
  source: string;
  isUrgentCancel?: boolean;
};

const InvoiceCancellationModal: React.FC<Props> = ({ open, setOpen, invoiceId, source, isUrgentCancel = false }) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);
  const mappedClinicName = useAppSelector((state) => state.root?.UIStates?.mappedClinicName);
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { invoiceListFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;

  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [clickedButton, setClickedButton] = useState(null);

  const { execute: fetchCancellationReason, cancellationReason } = useInvoiceCancellationListAPI();
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const { execute: cancelInvoice } = useInvoiceCancelAPI();
  const { execute: fetchInvoiceList } = useCartInvoiceListAPI();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    formState: { errors },
  } = useForm();

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {},
  });

  const store_pet_id = localStorage.getItem("selectedPetId");

  useEffect(() => {
    fetchCancellationReason();
  }, []);
  //   const selectedReason = watch("billingStatus");
  const cancellationReasonItems = useSelectMenuReducer(cancellationReason, "reason", "id");

  const onSubmit = (data, e) => {
    e.stopPropagation();
    e.preventDefault();
    let constructedBody: any = null;
    if (isUrgentCancel) {
      constructedBody = {
        invoice_id: invoiceId,
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        cancellation_reason: isOtherSelected ? data.otherReason : data.reason,
        cancellation_option: "urgent_cancel",
      };
    } else if (clickedButton === "cancelEdit") {
      constructedBody = {
        invoice_id: invoiceId,
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        cancellation_reason: isOtherSelected ? data.otherReason : data.reason,
        cancellation_option: "cancel_and_edit",
      };
    } else {
      constructedBody = {
        invoice_id: invoiceId,
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        cancellation_reason: isOtherSelected ? data.otherReason : data.reason,
        cancellation_option: "cancel_invoice",
      };
    }

    cancelInvoice(constructedBody).then(({ status, cartId }) => {
      if ((clickedButton === "cancelEdit" || isUrgentCancel) && status) {
        formHook.reset();
        setOpen(false);
        window.open(
          `/app/user-cart/cart-creation/${customerId}/${cartId}?from_cart=true&&pet_id=${store_pet_id}`,
          "_self",
        );
      } else {
        if (status) {
          formHook.reset();
          if (source === "payment_page") {
            dispatch(RESET_INVOICE_CART_SLICE());
            const constructedBody: IInvoiceProps = {
              user_id: customerId,

              start_date: null,
              end_date: null,
              statuses: [],
            };
            // window.open(`/app/user-cart/cart-billing/${customerId}from_cart=true`, "_self");
            const targetUrl = `/app/user-cart/cart-billing/${customerId}?from_cart=true`;
            window.location.href = targetUrl;
            fetchUserWalletDetails(customerId);
            fetchInvoiceList(constructedBody, "payment_page").then(({ stat }) => {
              if (stat) {
                setOpen(false);
              } else {
                setOpen(false);
              }
            });
          } else {
            fetchUserWalletDetails(customerId);
            const constructedBody: IInvoiceProps = {
              user_id: invoiceListFilter?.user_id,
              start_date: invoiceListFilter?.start_date,
              end_date: invoiceListFilter?.end_date,
              statuses: invoiceListFilter?.statuses,
            };
            fetchInvoiceList(constructedBody, "invoice_listing_page");
            setOpen(false);
          }
        }
      }
    });
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
            formHook.reset();
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <form
                onSubmit={formHook.handleSubmit(onSubmit)}
                className="inline-block w-full align-bottom bg-white rounded-2xl text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-md"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 pt-6">
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                        isUrgentCancel ? "bg-red-50" : "bg-amber-50"
                      }`}
                    >
                      <ExclamationTriangleIcon
                        className={`h-5 w-5 ${isUrgentCancel ? "text-[#F72F03]" : "text-amber-500"}`}
                      />
                    </span>
                    <div>
                      <h2 className="text-base font-semibold text-[#1F1F23]">
                        {isUrgentCancel ? "Urgent cancel this invoice?" : "Cancel this invoice?"}
                      </h2>
                      <p className="text-[#808DA0] text-xs mt-1 leading-relaxed">
                        This action can’t be undone. Please select a reason to continue.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      formHook.reset();
                      setOpen(false);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                  <label className="block text-xs font-medium text-[#1F1F23] mb-1.5">
                    Reason <span className="text-[#F72F03]">*</span>
                  </label>
                  <SearchableSelectMenu
                    errorMessages={[{ message: "", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("reason", item.title);
                        setIsOtherSelected(false);
                        if (item.title?.toLowerCase() === "others") {
                          setIsOtherSelected(true);
                        }
                      }
                    }}
                    fieldError={formHook.formState.errors.reason}
                    register={formHook.register("reason", { required: true })}
                    selectItems={cancellationReasonItems}
                    placeholder="Select reason"
                    showTooltips={true}
                    showTypedErrors={false}
                    showDropdownIcon={true}
                    defaultSelected={
                      cancellationReasonItems?.filter((oc) => oc.title === formHook.watch("reason"))[0]
                    }
                    listBoxClassName="w-full"
                    className="text-gray-800 w-full z-50"
                    containerClassName="w-full"
                  />
                  {errors.billingStatus && (
                    <p className="text-red-500 text-xs mt-1">{errors.billingStatus.message as any}</p>
                  )}

                  {isOtherSelected && (
                    <div className="mt-3">
                      <Controller
                        name="otherReason"
                        control={formHook.control} // Ensure you're using the correct form instance
                        rules={{
                          required: "Please provide a reason.",
                          validate: (value) => value.trim() !== "" || "Reason cannot be blank.",
                        }}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            rows={4}
                            placeholder="Enter your reason here"
                            className={`w-full p-2.5 border ${
                              formHook.formState.errors.otherReason ? "border-red-400" : "border-gray-300"
                            } rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#193A6A] focus:border-[#193A6A]`}
                          />
                        )}
                      />
                      {formHook.formState.errors.otherReason && (
                        <p className="text-red-500 text-[10px] mt-1">
                          {formHook.formState.errors.otherReason.message as any}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      formHook.reset();
                      setOpen(false);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-[#1F1F23] hover:bg-gray-50 transition-colors"
                  >
                    Keep Invoice
                  </button>
                  {isUrgentCancel ? (
                    <button
                      type="submit"
                      className="rounded-lg bg-[#F72F03] px-4 py-2 text-sm font-medium text-white hover:bg-[#d92802] transition-colors"
                    >
                      Urgent Cancel
                    </button>
                  ) : (
                    <button
                      type="submit"
                      onClick={() => {
                        setClickedButton("cancelEdit");
                      }}
                      className="rounded-lg bg-[#193A6A] px-4 py-2 text-sm font-medium text-white hover:bg-[#122d52] transition-colors"
                    >
                      Cancel & Edit
                    </button>
                  )}
                </div>
              </form>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default InvoiceCancellationModal;
